import { KeyboardEvent, useCallback, useEffect } from 'react';

import { Address } from '@this+that/models';

interface KeyboardActionHandlers {
  onEscape?: () => void;
  onEnter?: (event: KeyboardEvent) => void;
  onTab?: (event: KeyboardEvent) => void;
  onKey?: (key: string, event: KeyboardEvent) => void;
}

interface CustomKeyHandler {
  preventDefault?: boolean;
  handler: (event: KeyboardEvent) => void | boolean;
}

interface GenericKeyboardHandlerConfig {
  keyMappings?: Record<string, CustomKeyHandler>;
  fallbackHandler?: (key: string, event: KeyboardEvent) => void;
  globalPreventDefault?: boolean;
}

interface AddressesInputKeyboardHandlers {
  showSuggestions: boolean;
  suggestions: Address[];
  selectedSuggestionIndex: number;
  setSelectedSuggestionIndex: (
    value: number | ((prev: number) => number),
  ) => void;
  handleSelectSuggestion: (suggestion: Address) => void;
  handleAddAddress: () => void;
  setShowSuggestions: (show: boolean) => void;
  inputValue: string;
  addresses: Address[];
  setAddresses: (addresses: Address[]) => void;
}

interface UseKeyboardActionsOptions extends KeyboardActionHandlers {
  enabled?: boolean;
  preventDefault?: boolean;
  targetRef?: React.RefObject<HTMLElement | null>;
  eventType?: 'keydown' | 'keyup' | 'keypress';
}

interface UseGenericKeyboardActionsOptions
  extends GenericKeyboardHandlerConfig {
  enabled?: boolean;
  targetRef?: React.RefObject<HTMLElement | null>;
  eventType?: 'keydown' | 'keyup' | 'keypress';
}

export function useKeyboardActions(options: UseKeyboardActionsOptions = {}) {
  const {
    onEscape,
    onEnter,
    onTab,
    onKey,
    enabled = true,
    preventDefault = true,
    targetRef,
    eventType = 'keydown',
  } = options;

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const { key } = event;

      if (onKey) {
        onKey(key, event);
      }

      switch (key) {
        case 'Escape':
          if (onEscape) {
            if (preventDefault) event.preventDefault();
            onEscape();
          }
          break;
        case 'Enter':
          if (onEnter) {
            if (preventDefault) event.preventDefault();
            onEnter(event);
          }
          break;
        case 'Tab':
          if (onTab) {
            if (preventDefault) event.preventDefault();
            onTab(event);
          }
          break;
        default:
          break;
      }
    },
    [enabled, onEscape, onEnter, onTab, onKey, preventDefault],
  );

  useEffect(() => {
    if (!enabled || targetRef) return;

    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    document.addEventListener(eventType, listener);

    return () => {
      document.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  useEffect(() => {
    if (!enabled || !targetRef?.current) return;

    const element = targetRef.current;
    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    element.addEventListener(eventType, listener);

    return () => {
      element.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  return {
    handleKeyEvent,
  };
}

export function useGenericKeyboardActions(
  options: UseGenericKeyboardActionsOptions = {},
) {
  const {
    keyMappings,
    fallbackHandler,
    globalPreventDefault,
    enabled = true,
    targetRef,
    eventType = 'keydown',
  } = options;

  const handleKeyEvent = useCallback(
    createGenericKeyboardHandler({
      keyMappings,
      fallbackHandler,
      globalPreventDefault,
    }),
    [keyMappings, fallbackHandler, globalPreventDefault],
  );

  useEffect(() => {
    if (!enabled || targetRef) return;

    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    document.addEventListener(eventType, listener);

    return () => {
      document.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  useEffect(() => {
    if (!enabled || !targetRef?.current) return;

    const element = targetRef.current;
    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    element.addEventListener(eventType, listener);

    return () => {
      element.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  return {
    handleKeyEvent,
  };
}

export function createKeyboardHandler(
  handlers: KeyboardActionHandlers & { preventDefault?: boolean },
) {
  return (event: KeyboardEvent) => {
    const { key } = event;
    const { onEscape, onEnter, onTab, onKey, preventDefault = true } = handlers;

    if (onKey) {
      onKey(key, event);
    }

    switch (key) {
      case 'Escape':
        if (onEscape) {
          if (preventDefault) event.preventDefault();
          onEscape();
        }
        break;
      case 'Enter':
        if (onEnter) {
          if (preventDefault) event.preventDefault();
          onEnter(event);
        }
        break;
      case 'Tab':
        if (onTab) {
          if (preventDefault) event.preventDefault();
          onTab(event);
        }
        break;
      default:
        break;
    }
  };
}

export function createGenericKeyboardHandler(
  config: GenericKeyboardHandlerConfig = {},
) {
  return (event: KeyboardEvent) => {
    const {
      keyMappings = {},
      fallbackHandler,
      globalPreventDefault = false,
    } = config;
    const { key } = event;

    const customHandler = keyMappings[key];
    if (customHandler) {
      const shouldPreventDefault =
        customHandler.preventDefault ?? globalPreventDefault;
      if (shouldPreventDefault) {
        event.preventDefault();
      }

      const result = customHandler.handler(event);
      if (result === false) {
        return;
      }
    } else if (fallbackHandler) {
      fallbackHandler(key, event);
    }
  };
}

export function createAddressesInputKeyboardHandler(
  handlers: AddressesInputKeyboardHandlers,
) {
  const {
    showSuggestions,
    suggestions,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    handleSelectSuggestion,
    handleAddAddress,
    setShowSuggestions,
    inputValue,
    addresses,
    setAddresses,
  } = handlers;

  const keyMappings: Record<string, CustomKeyHandler> = {
    ArrowDown: {
      preventDefault: true,
      handler: () => {
        if (showSuggestions && suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => {
            if (prev === -1) return 0;
            return prev < suggestions.length - 1 ? prev + 1 : 0;
          });
          return false;
        }
      },
    },
    ArrowUp: {
      preventDefault: true,
      handler: () => {
        if (showSuggestions && suggestions.length > 0) {
          setSelectedSuggestionIndex((prev) => {
            if (prev === -1) return suggestions.length - 1;
            return prev > 0 ? prev - 1 : suggestions.length - 1;
          });
          return false;
        }
      },
    },
    Enter: {
      preventDefault: true,
      handler: () => {
        if (showSuggestions && suggestions.length > 0) {
          if (
            selectedSuggestionIndex >= 0 &&
            selectedSuggestionIndex < suggestions.length &&
            suggestions[selectedSuggestionIndex]
          ) {
            handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
            return false;
          }
          if (suggestions.length > 0) {
            handleSelectSuggestion(suggestions[0]);
            return false;
          }
        }
        handleAddAddress();
        return false;
      },
    },
    Escape: {
      preventDefault: true,
      handler: () => {
        if (showSuggestions) {
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          return false;
        }
      },
    },
    Tab: {
      preventDefault: false,
      handler: (event) => {
        if (
          showSuggestions &&
          suggestions.length > 0 &&
          selectedSuggestionIndex >= 0 &&
          selectedSuggestionIndex < suggestions.length &&
          suggestions[selectedSuggestionIndex]
        ) {
          event.preventDefault();
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
          return false;
        }

        if (inputValue.trim()) {
          event.preventDefault();
          handleAddAddress();
          return false;
        }
      },
    },
    ',': {
      preventDefault: true,
      handler: () => {
        handleAddAddress();
        return false;
      },
    },
    Backspace: {
      preventDefault: false,
      handler: (event) => {
        if (inputValue === '' && addresses.length) {
          event.preventDefault();
          setAddresses(addresses.slice(0, -1));
          return false;
        }
      },
    },
  };

  return createGenericKeyboardHandler({
    keyMappings,
    globalPreventDefault: false,
  });
}
